// Fuel Enquiry service — single object literal export following the
// ``budgetService.js`` shape. Sprint 1D (file 06 cache pattern + Q29 tab gate).
//
// Cache layer
// -----------
// In-memory ``Map`` keyed by ``<resource>:<commodityType>:<paramSignature>``.
// TTL is 60s for lookups (rarely change) and 15s for the enquiry list
// (frequent mutation while CTs are filing). Mirrors the
// ``cachedGroupingsByCommodity`` pattern from
// ``frontend/src/components/OperationsAccessList.js``.
//
// Lookups are NOT keyed by commodity_type — they are commodity-agnostic
// reference data (FuelType, FuelGrade, …) that the Solids flow doesn't
// even touch. The list cache IS keyed by commodityType to stay consistent
// with ``OperationsAccessList`` even though ``FuelEnquiry`` itself is
// fuels-only (per ``TRADE_OPS_SOLIDS_FUELS_SPLIT.md`` cache-invalidation §).
//
// Cache writes are best-effort: the list call always returns the live
// network response, but a copy is stashed for the next mount. Mutating
// actions (advance / rollback / archive / create / update) call
// ``invalidateEnquiries(commodityType)`` so the next list call hits the
// network rather than returning stale rows.
//
// API surface
// -----------
// ``list(params)`` returns the unwrapped DRF page envelope (``{ count,
// next, previous, results }``). The viewset's ``FuelEnquiryListPagination``
// only emits the envelope when ``?page`` is sent — so ``list({ page: 1,
// page_size: 25, ... })`` yields the envelope directly. We do not strip /
// massage the response.

import axiosInstance from '../utils/axiosConfig';

const BASE_URL = '/api/fuel-enquiries';

const ENQUIRY_LIST_TTL_MS = 15 * 1000;
const LOOKUP_TTL_MS = 60 * 1000;

const enquiryListCache = new Map();
const lookupCache = new Map();

const stableStringify = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'object') return String(value);
  const keys = Object.keys(value).sort();
  return JSON.stringify(keys.reduce((acc, k) => {
    if (value[k] !== undefined && value[k] !== null && value[k] !== '') {
      acc[k] = value[k];
    }
    return acc;
  }, {}));
};

const cacheGet = (cache, key, ttlMs) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > ttlMs) {
    cache.delete(key);
    return null;
  }
  return entry.value;
};

const cacheSet = (cache, key, value) => {
  cache.set(key, { value, ts: Date.now() });
};

export const invalidateEnquiries = (commodityType) => {
  if (!commodityType) {
    enquiryListCache.clear();
    return;
  }
  const prefix = `enquiries:${commodityType}:`;
  Array.from(enquiryListCache.keys()).forEach((k) => {
    if (k.startsWith(prefix)) enquiryListCache.delete(k);
  });
};

export const invalidateLookups = () => {
  lookupCache.clear();
};

const lookupFetch = async (resource) => {
  const key = `lookups:${resource}`;
  const cached = cacheGet(lookupCache, key, LOOKUP_TTL_MS);
  if (cached) return cached;
  const response = await axiosInstance.get(`${BASE_URL}/lookups/${resource}/`);
  const data = Array.isArray(response.data) ? response.data : (response.data?.results || []);
  cacheSet(lookupCache, key, data);
  return data;
};

export const fuelEnquiryService = {
  list: async (params = {}) => {
    const { commodityType = 'fuels', ...query } = params;
    const cacheKey = `enquiries:${commodityType}:${stableStringify(query)}`;
    const cached = cacheGet(enquiryListCache, cacheKey, ENQUIRY_LIST_TTL_MS);
    if (cached) return cached;

    const response = await axiosInstance.get(`${BASE_URL}/enquiries/`, { params: query });
    const data = response.data;

    let payload;
    if (data && typeof data === 'object' && Array.isArray(data.results)) {
      payload = data;
    } else if (Array.isArray(data)) {
      payload = {
        count: data.length,
        next: null,
        previous: null,
        results: data,
      };
    } else {
      payload = { count: 0, next: null, previous: null, results: [] };
    }

    cacheSet(enquiryListCache, cacheKey, payload);
    return payload;
  },

  retrieve: async (id) => {
    const response = await axiosInstance.get(`${BASE_URL}/enquiries/${id}/`);
    return response.data;
  },

  create: async (payload) => {
    const response = await axiosInstance.post(`${BASE_URL}/enquiries/`, payload);
    invalidateEnquiries('fuels');
    return response.data;
  },

  update: async (id, payload) => {
    const response = await axiosInstance.patch(`${BASE_URL}/enquiries/${id}/`, payload);
    invalidateEnquiries('fuels');
    return response.data;
  },

  // ``advance_stage`` returns one of three shapes:
  //   200 OK    — normal advance.        data is either the FuelEnquiry
  //               detail directly (legacy), OR
  //               { enquiry: <FuelEnquiry>, contracts?: {...} } when the
  //               Contract Creator Modal (bunkers_contracts Phase 3)
  //               minted a paired LegalContract set on this advance.
  //   202 Accepted — paused on credit.   data carries
  //                  { status: 'awaiting_credit_approval', enquiry_id,
  //                    stage, credit_status, credit_request_id, detail }.
  //                  The enquiry stays on ``draft`` until a CRM approver
  //                  actions the CIR; the FE shows the waiting banner.
  //   4xx       — validation / illegal transition.  Axios throws.
  //
  // We return a normalized envelope so call sites can tell the two
  // success branches apart without inspecting HTTP status codes:
  //   { paused: false, enquiry: <FuelEnquiry detail>, contracts?: {...} }
  //   { paused: true,  enquiry: null, awaiting: <202 payload> }
  //
  // ``contract_payloads`` is an optional 3rd argument used only by the
  // Contract Creator Modal for the ``with_supply_trader -> recapped``
  // transition. Shape per
  // ``feature_planning/bunkers_contracts/02_contract_creator_modal.md``.
  advanceStage: async (id, { target_stage, notes = '', contract_payloads, seco_confirmed } = {}) => {
    const body = { target_stage, notes };
    if (contract_payloads) body.contract_payloads = contract_payloads;
    // Bunkers KYC gate (July 2026): the recap step requires the SECO SESAM
    // sanctions confirmation ticked in the Contract Creator modal. A missing
    // flag is a hard 400 server-side; a KYC-blocked partner is a 409 (see
    // ``getKycClearanceStatus`` / ``requestKycClearance``).
    if (seco_confirmed) body.seco_confirmed = true;
    const response = await axiosInstance.post(
      `${BASE_URL}/enquiries/${id}/advance_stage/`,
      body
    );
    invalidateEnquiries('fuels');
    if (
      response.status === 202
      && response.data
      && response.data.status === 'awaiting_credit_approval'
    ) {
      return { paused: true, enquiry: null, awaiting: response.data };
    }
    // Detect the Phase 3 envelope (``{enquiry, contracts}``) vs the
    // legacy bare-enquiry shape. ``response.data.enquiry`` is truthy
    // only for the new shape because the FuelEnquiry serializer does
    // not emit a nested ``enquiry`` field on itself.
    const data = response.data;
    const isWrapped = data && typeof data === 'object' && data.enquiry && data.enquiry.id;
    const enquiry = isWrapped ? data.enquiry : data;
    const contracts = isWrapped ? (data.contracts || null) : null;
    return { paused: false, enquiry, contracts, awaiting: null };
  },

  // Bunkers KYC clearance gate (July 2026): precheck which CRM-linked partners
  // block a recap. Returns { blocked_partners: [{profile_id, name, reason}],
  // can_request }. Called before opening the Contract Creator modal so the
  // trader is shown the block modal instead of hitting a 409.
  getKycClearanceStatus: async (id) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/enquiries/${id}/kyc-clearance-status/`
    );
    return response.data;
  },

  // Raise KYC clearance requests for this enquiry's awaiting partners and
  // notify the KYC approvers. Idempotent server-side (a double-click won't
  // double-notify). Returns { created_profile_ids, pending_profile_ids,
  // notified_approvers }.
  requestKycClearance: async (id) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/enquiries/${id}/request-kyc-clearance/`,
      {}
    );
    return response.data;
  },

  rollbackStage: async (id, { notes = '' } = {}) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/enquiries/${id}/rollback_stage/`,
      { notes }
    );
    invalidateEnquiries('fuels');
    return response.data;
  },

  archive: async (id) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/enquiries/${id}/archive/`
    );
    invalidateEnquiries('fuels');
    return response.data;
  },

  // Archiving was one-way: nothing in the app could undo it, so the only way
  // back was a direct database edit. Same owner/HOD/superuser gate as
  // ``archive`` on the server.
  unarchive: async (id) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/enquiries/${id}/unarchive/`
    );
    invalidateEnquiries('fuels');
    return response.data;
  },

  // Customer Confirmation PDF (bunkers counterpart of the Cargo Bid & Offer
  // confirmation). Returns the raw PDF as a Blob so the caller can build an
  // object URL for the preview modal and the download button. Mirrors the
  // SOA blob-download pattern in ``statementService.js`` (responseType blob).
  customerConfirmationPdf: async (id) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/enquiries/${id}/customer-confirmation-pdf/`,
      { responseType: 'blob' },
    );
    return response.data;
  },

  // Supplier Nomination PDF (the BUY/PURCHASE-leg sibling of the Customer
  // Confirmation — addressed to the physical supplier, branded with the HMS
  // buying entity). Returns the raw PDF as a Blob so the caller can build an
  // object URL for the preview modal and the download button. Mirrors
  // ``customerConfirmationPdf`` (responseType blob).
  supplierNominationPdf: async (id) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/enquiries/${id}/supplier-nomination-pdf/`,
      { responseType: 'blob' },
    );
    return response.data;
  },

  // Trade-document dispatches (Phase 5/7). The list endpoint returns a flat
  // JSON array (no DRF page envelope) filtered by ``?enquiry=<id>``; each row
  // carries the doc_type, status, assignee + resolved recipient the Review &
  // Send modal renders. Not cached -- dispatch status flips the moment a CT
  // hits Send, so the form / notification always reads live rows.
  listDispatches: async (enquiryId) => {
    const response = await axiosInstance.get(`${BASE_URL}/dispatches/`, {
      params: { enquiry: enquiryId },
    });
    return Array.isArray(response.data)
      ? response.data
      : (response.data?.results || []);
  },

  // Send a single dispatch to one or more recipients. ``recipients`` may be a
  // string (single address, back-compat) or an array of addresses; either way
  // it's posted as the ``recipient_emails`` array the backend reads (it still
  // accepts the legacy ``recipient_email`` single key). The backend regenerates
  // the PDF, sends the branded email TO all addresses (CC stays trader+branch)
  // and flips the row to ``sent`` (200 with the updated dispatch) or ``failed``
  // (502). 400 = empty/invalid recipient list, 403 = not assignee/branch-
  // manager/staff. Axios throws on 4xx/5xx and the caller surfaces
  // ``error.response.data.error`` inline.
  sendDispatch: async (dispatchId, recipients) => {
    const recipientEmails = Array.isArray(recipients)
      ? recipients
      : [recipients].filter(Boolean);
    const response = await axiosInstance.post(
      `${BASE_URL}/dispatches/${dispatchId}/send/`,
      { recipient_emails: recipientEmails },
    );
    return response.data;
  },

  // Raise the next version of an already-SENT document. The deal moved after
  // the confirmation went out, so the counterparty needs a corrected one; the
  // re-issue is headed "Revision N" and carries the current values, with no
  // change marking. Returns the new dispatch row.
  reviseDispatch: async (dispatchId) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/dispatches/${dispatchId}/revise/`,
      {},
    );
    return response.data;
  },

  // Editable-field spec for a dispatch's document (Customer Confirmation /
  // Supplier Nomination). Returns ``{ fields: [{key, label, sensitive, value}],
  // overrides: {field_key: value}, editable }`` where ``value`` is the pure
  // DEFAULT and ``overrides`` is the trader's saved edits — so the modal can
  // pre-fill the edit form and persist only what actually changed vs default.
  dispatchEditableFields: async (dispatchId) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/dispatches/${dispatchId}/editable-fields/`,
    );
    return response.data;
  },

  // Persist the trader's field edits on a dispatch (pre-send). ``overrides`` is
  // a ``{field_key: value}`` map; the backend keeps only the known editable
  // keys and refuses (409) once the document is sent. Returns the updated row.
  saveDispatchOverrides: async (dispatchId, overrides) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/dispatches/${dispatchId}/save-overrides/`,
      { field_overrides: overrides || {} },
    );
    return response.data;
  },

  // Render a dispatch's PDF with its stored overrides applied -- the exact
  // bytes ``send`` will email (preview == sent). Returns a Blob for the preview
  // iframe.
  //
  // Once the dispatch IS sent the same URL stops previewing and starts serving
  // the bytes that were actually emailed, so "View sent document" doesn't drift
  // as the deal moves.
  dispatchPreviewPdf: async (dispatchId) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/dispatches/${dispatchId}/preview/`,
      { responseType: 'blob' },
    );
    return response.data;
  },

  // Relationship graph for the enquiry (Sprint follow-up to recap/deal
  // graphs). Returns ``{ nodes, edges }`` in the exact shape consumed by
  // ``InvoiceRelationshipModal`` so the same React Flow renderer handles
  // it without a special branch.
  getRelationships: async (id) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/enquiries/${id}/get_relationships/`
    );
    return response.data;
  },

  // Multi-enquiry grouping. Mirrors the recap-grouping pattern used by
  // ``offer.update_trade_recap_groups`` — collection-level POST that
  // stamps / clears a shared ``enquiry_grouping_identifier`` (``EG-####``)
  // across the supplied enquiry ids. The backend enforces same-customer
  // + same-delivery-window on group creation.
  groupEnquiries: async (ids) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/enquiries/group/`,
      { enquiry_ids: ids },
    );
    invalidateEnquiries('fuels');
    return response.data;
  },

  ungroupEnquiries: async (ids) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/enquiries/ungroup/`,
      { enquiry_ids: ids },
    );
    invalidateEnquiries('fuels');
    return response.data;
  },

  lookups: {
    fuelTypes: () => lookupFetch('fuel-types'),
    fuelGrades: () => lookupFetch('fuel-grades'),
    sulfurLimits: () => lookupFetch('sulfur-limits'),
    iso8217Years: () => lookupFetch('iso-8217-years'),
    // System-wide ``employees.Unit`` rows (the same set HMS Admin ->
    // System data -> Units manages) are the canonical measuring-unit
    // source for fuel enquiries. The legacy ``measuring-units`` lookup
    // (orphaned ``fuel_enquiries.MeasuringUnit`` rows) is kept around
    // so existing imports don't break, but the form always reads
    // ``measuringUnits`` here.
    measuringUnits: () => lookupFetch('units'),
    legacyMeasuringUnits: () => lookupFetch('measuring-units'),
    thirdPartyCostCategories: () => lookupFetch('third-party-cost-categories'),
    vesselTypes: () => lookupFetch('vessel-types'),
  },

  branches: {
    list: async () => {
      const response = await axiosInstance.get(`${BASE_URL}/branches/`);
      return Array.isArray(response.data) ? response.data : (response.data?.results || []);
    },
    staff: async (branchId, payload) => {
      const response = await axiosInstance.post(
        `${BASE_URL}/branches/${branchId}/staff/`,
        payload
      );
      return response.data;
    },
  },

  // Bunkers 2-screen Section 7. Reads come off the enquiry detail
  // payload (``enquiry.third_party_costs``); writes go directly to the
  // ``ThirdPartyCostViewSet`` here. The viewset enforces the visibility
  // matrix + cross-row currency lock server-side, so we just forward
  // Time-boxed stand-down of the bunkers KYC recap gate. GET is readable by
  // any Bunkers user because the enquiry screen must be able to warn that the
  // gate is currently down; opening/closing a window is superuser-only and is
  // enforced server-side, not here.
  kycGateBypass: {
    get: async () => {
      const response = await axiosInstance.get(`${BASE_URL}/kyc-gate-bypass/`);
      return response.data;
    },
    open: async (payload) => {
      const response = await axiosInstance.post(
        `${BASE_URL}/kyc-gate-bypass/`,
        payload,
      );
      return response.data;
    },
    close: async () => {
      const response = await axiosInstance.delete(`${BASE_URL}/kyc-gate-bypass/`);
      return response.data;
    },
  },

  // the form payload as-is. After every mutation we re-fetch the
  // enquiry detail (in the form) -- no shared cache to invalidate
  // because TPC rows aren't returned by the list endpoint.
  thirdPartyCosts: {
    create: async (payload) => {
      const response = await axiosInstance.post(
        `${BASE_URL}/third-party-costs/`,
        payload,
      );
      return response.data;
    },
    update: async (id, payload) => {
      const response = await axiosInstance.patch(
        `${BASE_URL}/third-party-costs/${id}/`,
        payload,
      );
      return response.data;
    },
    remove: async (id) => {
      await axiosInstance.delete(`${BASE_URL}/third-party-costs/${id}/`);
    },
  },
};

export default fuelEnquiryService;
